import request from '../utils/request';
import { Message } from '../store/chatStore';

export interface ChatRequest {
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
  temperature?: number;
  max_tokens?: number;
}

export interface ChatResponse {
  content: string;
  finish_reason?: string;
}

/**
 * 发送聊天消息（流式响应 - 使用EventSource/SSE）
 * @param conversationId 对话ID
 * @param content 用户消息内容
 * @param onMessage 接收消息的回调函数
 * @param onMedia 接收媒体信息的回调函数
 * @param onError 错误回调
 * @param onComplete 完成回调
 * @param temperature 温度参数
 * @param maxTokens 最大token数
 */
export const sendChatMessageStream = async (
  conversationId: number | string,
  content: string,
  onMessage: (content: string) => void,
  onMedia?: (media: { images: string | null; videos: string | null }) => void,
  onError?: (error: Error) => void,
  onComplete?: () => void,
  temperature: number = 0.7,
  maxTokens: number = 2000
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const token = localStorage.getItem('token');
      // 与 request.ts 一致：生产环境用相对路径 /api，与页面同源，避免 CORS 与 loopback 限制
      const baseURL = process.env.REACT_APP_API_BASE_URL ?? '/api';

      // 使用fetch API + ReadableStream接收SSE流式响应
      // 注意：EventSource不支持自定义headers，所以使用fetch API
      const controller = new AbortController();
      
      const requestUrl = `${baseURL}/chat/stream`;
      console.log('Sending chat request to:', requestUrl, { conversationId, hasToken: !!token });
      
      fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          content,
          temperature,
          max_tokens: maxTokens,
        }),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            // 处理错误响应
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
            
            console.error('Chat request failed:', {
              status: response.status,
              statusText: response.statusText,
              error: errorData,
              url: requestUrl
            });
            
            // 根据状态码处理
            if (response.status === 401) {
              throw new Error('未授权，请重新登录');
            } else if (response.status === 404) {
              // 404可能是路由不存在或对话不存在
              const message = errorData.message || errorData.error || '请求的资源不存在';
              throw new Error(`404错误: ${message}。请检查对话ID是否正确，或联系管理员。`);
            } else if (response.status === 403) {
              const err = new Error(
                errorData.message || '未登录用户在同一对话中最多可追问 3 轮，请登录后继续'
              );
              (err as Error & { requiresLogin?: boolean }).requiresLogin =
                errorData.requiresLogin === true || errorData.error === 'guest_round_limit';
              throw err;
            } else if (response.status === 429) {
              throw new Error('请求过于频繁，请稍后再试');
            } else if (response.status === 500) {
              throw new Error('服务器错误，请稍后重试');
            } else {
              throw new Error(errorMessage);
            }
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('Response body is not readable');
          }

          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;
              
              if (line.startsWith('data: ')) {
                const data = line.slice(6).trim();
                
                if (data === '[DONE]') {
                  onComplete?.();
                  resolve();
                  return;
                }

                try {
                  const json = JSON.parse(data);
                  if (json.content) {
                    onMessage(json.content);
                  }
                  if (json.media) {
                    onMedia?.(json.media);
                  }
                  // 处理错误信息
                  if (json.error) {
                    throw new Error(json.error);
                  }
                } catch (e) {
                  // 如果不是JSON解析错误，则抛出
                  if (e instanceof Error && !e.message.includes('JSON')) {
                    throw e;
                  }
                  // 忽略JSON解析错误，继续处理下一行
                }
              }
            }
          }

          onComplete?.();
          resolve();
        })
        .catch((error) => {
          const err = error instanceof Error ? error : new Error('Unknown error');
          onError?.(err);
          reject(err);
        });

      // 返回取消函数（如果需要）
      return () => controller.abort();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      onError?.(err);
      reject(err);
    }
  });
};

/**
 * 发送聊天消息（非流式响应，备用方案）
 */
export const sendChatMessage = async (
  messages: ChatRequest['messages'],
  temperature: number = 0.7,
  maxTokens: number = 2000
): Promise<ChatResponse> => {
  return request.post('/chat', {
    messages,
    temperature,
    max_tokens: maxTokens,
  });
};
